import { zodResolver } from "@hookform/resolvers/zod";
import { Grid2 } from "@mui/material";
import DropdownV2 from "components/DropdownComponentV2";
import DropdownMultipleComponent from "components/DropdownMultipleComponent";
import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
	Controller,
	type DefaultValues,
	useForm
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useGetAllResponsiblesForSelectionQuery } from "services/Recognition/Responsible/hooks/useQuery";
import { formatCpf } from "utils/formatters";
import type { z } from "zod";
import ButtonComponent from "../../../../../components/ButtonComponent";
import CheckboxComponent from "../../../../../components/CheckboxComponent";
import DatePickerComponent from "../../../../../components/DatePickerComponent";
import DropdownSelectComponent from "../../../../../components/DropdownSelectComponent";
import TextFieldComponent from "../../../../../components/TextFieldComponent";
import { AppContext } from "../../../../../context/AppContextProvider";
import { useGetAllDeviceGroupByLicenseAsync } from "../../../../../services/DeviceGroupService/hooks/useMutation";
import type { IDeviceGroupSimpleProps } from "../../../../../services/DeviceGroupService/types";
import { useGetAllControllersForSelectionMutateAsync } from "../../../../../services/Devices/Recognition/Controllers/hooks/useMutation";
import { useGetAllRecognitionGroupByLicenseAsync } from "../../../../../services/Recognition/Group/hooks/useMutations";
import type { IRecognitionGroupSimpleProps } from "../../../../../services/Recognition/Group/types";
import {
	useCreateStudentRecognitionAsync,
	useCreateStudentRecognitionAsyncJob,
	useGetStudentRecognitionByIdAsync,
	useUpdateStudentRecognitionAsync,
	useUpdateStudentRecognitionAsyncJob,
} from "../../../../../services/Recognition/Student/hooks/useMutation";
import { useFaceSyncLegacyFlag } from "../../../../../services/Configs/useFaceSyncLegacyFlag";
import { useSyncContext } from "../../../../../context/SyncJobContext";
import { StudentRecognitionFormSchema } from "../../../../../services/Recognition/Student/schema";
import {
	type IStudentRecognitionCreateProps,
	type IStudentRecognitionProps,
	type IStudentRecognitionUpdateProps,
	StudentGenderTypes,
} from "../../../../../services/Recognition/Student/types";
import type { IRecognitionSimpleProps } from "../../../../../services/Recognition/types";
import { useGetAllSchoolClassByLicenseAsync } from "../../../../../services/SchoolClass/hooks/useMutation";
import type { ISchoolClassSimpleProps } from "../../../../../services/SchoolClass/types";
import type { ISelectionOption } from "../../../../../services/types";
import { transformFirebaseLinkToBlob } from "../../../../../utils/TransformUtils";
import { FaceEditionContext } from "../../../context/FaceEditionContextProvider";
import type { SyncOperationKind } from "../../utils";
import ImageFace from "../cells/ImageFace";
import { UC_OPTIONS } from "../../../../../config/ucOptions";

const DEFAULT_FORM_VALUES: DefaultValues<StudentRecognitionFormInput> = {
	name: "",
	cpf: "",
	image: "",
	registration: "",
	gender: "",
	recognitionGroupIds: [],
	facialControllerIds: [],
	deviceGroupIds: [],
	responsibleIds: [],
	canGoOut: false,
	classId: [],
	cards: [],
	uc: [],
};

const ONLY_DIGITS_REGEX = /\D/g;

interface IStudentRecognitionEditFormProps {
	selectedRecognitionId?: number;
	handleSaveForm: () => void;
	selectedRecognitionData?: IRecognitionSimpleProps;
}

type StudentRecognitionFormInput = z.infer<typeof StudentRecognitionFormSchema>;

const StudentRecognitionEditForm = ({
	handleSaveForm,
	selectedRecognitionId,
	selectedRecognitionData,
}: IStudentRecognitionEditFormProps) => {
	const { t } = useTranslation();

	const isEditMode = !!selectedRecognitionId;

	const form = useForm<StudentRecognitionFormInput>({
		resolver: zodResolver(StudentRecognitionFormSchema),
		defaultValues: DEFAULT_FORM_VALUES,
	});

	const {
		control,
		reset,
		handleSubmit,
		setValue,
		formState: { errors },
	} = form;

	const genderOptions = useMemo(
		() => [
			{
				label: t("recognitionPage.modalEditFace.genders.male"),
				value: StudentGenderTypes.MALE,
			},
			{
				label: t("recognitionPage.modalEditFace.genders.female"),
				value: StudentGenderTypes.FEMALE,
			},
			{
				label: t("recognitionPage.modalEditFace.genders.other"),
				value: StudentGenderTypes.OTHER,
			},
		],
		[t],
	);

	const [classOptions, setClassOptions] = useState<ISchoolClassSimpleProps[]>(
		[],
	);
	const [groups, setGroups] = useState<IRecognitionGroupSimpleProps[]>([]);
	const [deviceGroups, setDeviceGroups] = useState<IDeviceGroupSimpleProps[]>(
		[],
	);
	const [controllers, setControllers] = useState<ISelectionOption[]>([]);

	const { selectedImage, setSelectedImage, isImageChange } =
		useContext(FaceEditionContext);
	const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

	const {
		mutateAsync: mutateGetRecognition,
		isLoading: isLoadingGetRecognition,
	} = useGetStudentRecognitionByIdAsync();
	const {
		mutateAsync: mutateCreateRecognitionJob,
		isLoading: isLoadingCreateRecognition,
	} = useCreateStudentRecognitionAsyncJob();
	const { mutateAsync: mutateCreateRecognitionLegacy } = useCreateStudentRecognitionAsync();
	const syncCtx = useSyncContext();
	const {
		mutateAsync: mutateUpdateRecognitionJob,
		isLoading: isLoadingUpdateRecognitionJob,
	} = useUpdateStudentRecognitionAsyncJob();
	const { mutateAsync: mutateUpdateRecognitionLegacy } = useUpdateStudentRecognitionAsync();
	const useLegacy = useFaceSyncLegacyFlag();
	const { mutateAsync: mutateGetRecognitionGroups } =
		useGetAllRecognitionGroupByLicenseAsync();
	const { mutateAsync: mutateGetControllersForSelectionAsync } =
		useGetAllControllersForSelectionMutateAsync();
	const { mutateAsync: mutateGetDeviceGroups } =
		useGetAllDeviceGroupByLicenseAsync();
	const { mutateAsync: mutateGetClass } = useGetAllSchoolClassByLicenseAsync();

	const { 
		data: responsibleOptions = [], 
		isLoading: isLoadingResponsibles 
	} = useGetAllResponsiblesForSelectionQuery();

	useEffect(() => {
		const loadDropdownData = async () => {
			try {
				const [
					classData,
					groupsData,
					controllersData,
					deviceGroupsData,
				] = await Promise.all([
					mutateGetClass(undefined),
					mutateGetRecognitionGroups({}),
					mutateGetControllersForSelectionAsync(),
					mutateGetDeviceGroups(),
				]);
				setClassOptions(classData || []);
				setGroups(groupsData || []);
				setControllers(controllersData || []);
				setDeviceGroups(deviceGroupsData || []);
			} catch (error) {
				console.error("Erro ao carregar listas do form de Estudante", error);
				showSnackbar("Erro ao carregar listas de seleção.", "error");
			}
		};
		loadDropdownData();
	}, []);

	useEffect(() => {
		let isMounted = true;

		if (selectedRecognitionId) {
			mutateGetRecognition(selectedRecognitionId)
				.then(async (response) => {
					if (!isMounted) return;
					const formValues = mapRecognitionToForm(response);
					reset(formValues);

					if (response.image) {
						const imageBlob = await transformFirebaseLinkToBlob(response.image);
						if (isMounted) setSelectedImage(imageBlob);
					}
				})
				.catch((error) => {
					if (!isMounted) return;
					console.error("Erro ao carregar dados da face", error);
					showSnackbar(t("common.errorLoadingData"), "error");
				});
		}

		return () => {
			isMounted = false;
			setSelectedImage("");
		};
	}, [selectedRecognitionId]);

	useEffect(() => {
		if (!selectedRecognitionId && selectedRecognitionData) {
			if (selectedRecognitionData.image) {
				setSelectedImage(selectedRecognitionData.image);
				setValue("name", selectedRecognitionData.name);
			}
			if (selectedRecognitionData.registration) {
				setValue("registration", selectedRecognitionData.registration);
			}
			if (selectedRecognitionData.facialControllersIds) {
				setValue(
					"facialControllerIds",
					selectedRecognitionData.facialControllersIds,
				);
			}
		}
	}, [
		selectedRecognitionId,
		selectedRecognitionData,
		setValue,
		setSelectedImage,
	]);

	const convertImageToBase64 = useCallback(
		(image: string | Blob): Promise<string> => {
			return new Promise((resolve, reject) => {
				if (image instanceof Blob) {
					const reader = new FileReader();
					reader.onloadend = () => resolve(reader.result as string);
					reader.onerror = reject;
					reader.readAsDataURL(image);
				} else {
					resolve(image);
				}
			});
		},
		[],
	);

	const buildPayload = useCallback(
		async (data: StudentRecognitionFormInput) => {
			const payload = { ...data };

			if (isImageChange) {
				payload.image = "";
			} else {
				const encodedImage = await convertImageToBase64(
					selectedImage as string | Blob,
				);
				const isFirebaseUrl = encodedImage?.startsWith(
					"https://firebasestorage.googleapis.com/",
				);
				payload.image = isFirebaseUrl ? "" : encodedImage;
			}

			if (payload.validUntil)
				payload.validUntil = payload.validUntil.trim().split("T")[0];
			if (payload.birthDate)
				payload.birthDate = payload.birthDate.trim().split("T")[0];
			if (payload.cpf) payload.cpf = payload.cpf.replace(ONLY_DIGITS_REGEX, "");

			payload.uc = payload.uc ?? [];

			return payload;
		},
		[selectedImage, isImageChange, convertImageToBase64],
	);

	const mapRecognitionToForm = (
		response: IStudentRecognitionProps,
	): StudentRecognitionFormInput => {
		return {
			id: response.id,
			name: response.name,
			cpf: response.cpf ? formatCpf(response.cpf) : "",
			image: response.image ?? "",
			recognitionGroupIds: response.recognitionGroups?.map((i) => i.id) ?? [],
			facialControllerIds: response.facialControllers?.map((i) => i.id) ?? [],
			deviceGroupIds: response.deviceGroups?.map((i) => i.id) ?? [],
			validUntil: response.validUntil
				? dayjs(response.validUntil).toISOString()
				: null,
			birthDate: response.birthDate
				? dayjs(response.birthDate).toISOString()
				: "",
			gender: response.gender,
			registration: response.registration ?? "",
			responsibleIds: response.responsibleIds,
			canGoOut: response.canGoOut,
			uc: Array.isArray(response.uc)
				? response.uc
				: response.uc
					? [response.uc]
					: [],
			classId: response.classId ?? [],
			cards: [],
		};
	};

	const handleSave = useCallback(
		async (data: StudentRecognitionFormInput) => {
			if (!selectedImage) {
				showSnackbar(
					t("recognitionPage.registerFace.youForgotToTakeAPicture"),
					"error",
				);
				return;
			}

			const operation: SyncOperationKind = data.id ? "update" : "create";

			if (useLegacy) {
				setIsShowLoadingComponent(true);
				try {
					const sanitizedData = await buildPayload(data);
					const payload = { ...sanitizedData, file: selectedImage as Blob };

					if (data.id) {
						await mutateUpdateRecognitionLegacy({
							id: data.id,
							data: payload as IStudentRecognitionUpdateProps,
						});
						showSnackbar("Cadastro atualizado com sucesso!", "success");
					} else {
						await mutateCreateRecognitionLegacy(
							payload as IStudentRecognitionCreateProps,
						);
						showSnackbar("Cadastro salvo com sucesso!", "success");
					}

					setSelectedImage("");
					reset();
					handleSaveForm();
				} catch (error: any) {
					const errorMessage = error instanceof Error && error.message !== 'Erro interno do servidor!'
						? error.message
						: t("common.errorSaving");

					showSnackbar(errorMessage, "error");
				} finally {
					setIsShowLoadingComponent(false);
				}
				return;
			}

			syncCtx.startPreparing(data.name, operation);
			try {
				const sanitizedData = await buildPayload(data);
				const payload = { ...sanitizedData, file: selectedImage as Blob };

				if (data.id) {
					const job = await mutateUpdateRecognitionJob({
						id: data.id,
						data: payload as IStudentRecognitionUpdateProps,
					});
					syncCtx.setSyncJob(job.jobId, job.recognitionId, data.name, 0, job.message ?? "Enfileirado para processamento...", operation);
					syncCtx.hideToast();
				} else {
					const job = await mutateCreateRecognitionJob(
						payload as IStudentRecognitionCreateProps,
					);
					syncCtx.setSyncJob(job.jobId, job.recognitionId, data.name, 0, job.message ?? "Enfileirado para processamento...", operation);
					syncCtx.hideToast();
				}

				setSelectedImage("");
				reset();
				handleSaveForm();
			} catch (error: any) {
				console.error("Erro ao salvar face", error);
				syncCtx.stopPreparing();

				const errorMessage = error instanceof Error && error.message !== 'Erro interno do servidor!'
					? error.message
					: t("common.errorSaving");

				showSnackbar(errorMessage, "error");
			}
		},
		[
			useLegacy,
			selectedImage,
			buildPayload,
			mutateUpdateRecognitionJob,
			mutateCreateRecognitionJob,
			mutateUpdateRecognitionLegacy,
			mutateCreateRecognitionLegacy,
			syncCtx,
			setSelectedImage,
			setIsShowLoadingComponent,
			reset,
			handleSaveForm,
			showSnackbar,
			t,
		],
	);

	return (
		<>
			<Grid2
				container
				direction={"column"}
				justifyContent={"center"}
				alignItems={"center"}
				size={12}
				spacing={3}
			>
				<Grid2
					display={"flex"}
					flexDirection={{ xs: "column", md: "row" }}
					justifyContent={"center"}
					alignItems={{ xs: "center", md: "normal" }}
					size={12}
					gap={{ xs: 0, md: 3 }}
				>
					<Grid2 size={{ xs: 12, lg: 6 }}>
						<Controller
							name={"name"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldComponent
									label={`${t("recognitionPage.modalEditFace.name")} *`}
									onChangeText={onChange}
									type={"text"}
									errorMessage={error?.message}
									value={value}
								/>
							)}
						/>

						<Controller
							name={"cpf"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldComponent
									label={`${t("recognitionPage.modalEditFace.cpf")} *`}
									onChangeText={onChange}
									type={"text"}
									errorMessage={error?.message}
									value={value}
									mask={"000.000.000-00"}
								/>
							)}
						/>

						<Controller
							name={"registration"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldComponent
									label={`${t("recognitionPage.modalEditFace.registration")} *`}
									onChangeText={onChange}
									type={"text"}
									errorMessage={error?.message}
									value={value}
								/>
							)}
						/>

						<Controller
							name={"birthDate"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DatePickerComponent
									value={value ? dayjs(value).utc() : null}
									onChange={(dateValue) => {
										const date = dayjs(dateValue);
										onChange(
											date.isValid() ? dayjs(dateValue).toISOString() : null,
										);
									}}
									errorMessage={error?.message}
									label={`${t("recognitionPage.modalEditFace.dateOfBirth")} *`}
									disableFuture={true}
								/>
							)}
						/>

						<Controller
							name={"validUntil"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DatePickerComponent
									value={value ? dayjs(value).utc() : null}
									onChange={(dateVal) => {
										const date = dayjs(dateVal);
										onChange(date.isValid() ? date.toISOString() : null);
									}}
									errorMessage={error?.message}
									label={t("recognitionPage.modalEditFace.faceValidity")}
									isOptional={true}
									disablePast={true}
								/>
							)}
						/>

						<Controller
							name="uc"
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownSelectComponent
									label={t("recognitionPage.modalEditFace.corporateUnity")}
									options={UC_OPTIONS}
									labelField={"label"}
									valueField={"value"}
									errorMessage={error?.message}
									selectedValues={value || []}
									onSelectedValues={onChange}
								/>
							)}
						/>
					</Grid2>

					<Grid2
						size={{ xs: 12, lg: 6 }}
						display={"column"}
						alignItems={"center"}
						justifyContent={"center"}
					>
						<Controller
							name={"recognitionGroupIds"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownSelectComponent
									label={t("recognitionPage.modalEditFace.faceGroup")}
									options={groups}
									labelField={"name"}
									valueField={"id"}
									errorMessage={error?.message}
									selectedValues={value}
									onSelectedValues={onChange}
								/>
							)}
						/>

						<Controller
							name={"facialControllerIds"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownSelectComponent
									label={t("recognitionPage.modalEditFace.facialController")}
									options={controllers}
									labelField={"name"}
									valueField={"id"}
									errorMessage={error?.message}
									selectedValues={value}
									onSelectedValues={onChange}
								/>
							)}
						/>

						<Controller
							name={"deviceGroupIds"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownSelectComponent
									label={t("recognitionPage.modalEditFace.deviceGroup")}
									options={deviceGroups}
									labelField={"name"}
									valueField={"id"}
									errorMessage={error?.message}
									selectedValues={value}
									onSelectedValues={onChange}
								/>
							)}
						/>

						<Controller
							name={"responsibleIds"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => {
								const safeValues = Array.isArray(value) ? value : [];
								
								return (
									<DropdownMultipleComponent
										options={responsibleOptions}
										label={t("recognitionPage.modalEditFace.responsible")}
										labelField="name"
										valueField="id"
										errorMessage={error?.message}
										value={safeValues}
										onChange={onChange}
										optionsLimit={100}
										isLoading={isLoadingResponsibles}
									/>
								);
							}}
						/>

						<Controller
							name={"classId"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error, invalid },
							}) => (
								<DropdownSelectComponent
									options={classOptions}
									label={t("recognitionPage.modalEditFace.class")}
									labelField={"name"}
									valueField={"id"}
									error={invalid}
									errorMessage={error?.message}
									selectedValues={value as any[]}
									onSelectedValues={onChange}
								/>
							)}
						/>

						<Controller
							name={"gender"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownV2
									label={`${t("recognitionPage.modalEditFace.genders.gender")} *`}
									value={value}
									errorMessage={error?.message}
									labelField={"label"}
									valueField={"value"}
									options={genderOptions}
									onChange={onChange}
								/>
							)}
						/>

						<Grid2 size={6} sx={{ pl: 1 }}>
							<Controller
								name={"canGoOut"}
								control={control}
								render={({ field: { value, onChange } }) => (
									<CheckboxComponent
										label={t("recognitionPage.modalEditFace.cantLeave")}
										value={value ?? undefined}
										onChange={onChange}
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</Grid2>

				<Grid2
					size={8}
					display={"column"}
					alignItems={"center"}
					justifyContent={"center"}
					mb={3}
				>
					<ImageFace handleCancelImageEdition={() => setSelectedImage("")} />
				</Grid2>
			</Grid2>

			<ButtonComponent
				variant="contained"
				color="primary"
				loading={
					isLoadingUpdateRecognitionJob ||
					isLoadingCreateRecognition ||
					isLoadingGetRecognition
				}
				fullWidth
				onClick={handleSubmit(handleSave)}
			>
				{t("recognitionPage.modalEditFace.buttonCaptureImage.save")}
			</ButtonComponent>
		</>
	);
};

export default StudentRecognitionEditForm;